import { createSelector } from 'reselect';

import { replicationModesSelector } from '../selectors';
import { HOUSE_MODES, SECURITY_MODES } from 'constants/MeshbotConstant';
import {
    EZLOGIC_TITLE_HOME,
    EZLOGIC_TITLE_AWAY,
    EZLOGIC_TITLE_NIGHT,
    EZLOGIC_TITLE_VACATION,
} from 'constants/language_tokens';
import { OEM_IDS } from 'services/oem/src/constants/oems';
import oemManager from 'services/oem';

/**
 * Mapping of API mode names to translation keys.
 * @constant {Object.<string, string>}
 */
const HOUSE_MODE_NAME_TO_TRANSLATION_KEY = {
    Home: EZLOGIC_TITLE_HOME,
    Away: EZLOGIC_TITLE_AWAY,
    Night: EZLOGIC_TITLE_NIGHT,
    Vacation: EZLOGIC_TITLE_VACATION,
};

/**
 * Transforms API mode objects to UI-compatible format.
 *
 * @param {Array|null|undefined} apiModes - Array of mode objects from the API.
 * @param {string} apiModes[]._id - The unique identifier of the mode.
 * @param {string} apiModes[].name - The name of the mode (e.g., 'Home', 'Away').
 * @returns {Array|null} Transformed array of mode objects, or null if input is empty/invalid.
 *
 * @example
 * const apiModes = [{ _id: '1', name: 'Home' }, { _id: '2', name: 'Away' }];
 * const uiModes = transformApiModesToUiFormat(apiModes);
 * // Returns: [
 * //   { id: 'Home_1', name: 'ezlogic.title.home', value: '1' },
 * //   { id: 'Away_2', name: 'ezlogic.title.away', value: '2' }
 * // ]
 */
const transformApiModesToUiFormat = (apiModes) => {
    if (!apiModes?.length) {
        return null;
    }

    return apiModes.map((mode) => ({
        id: `${mode.name}_${mode._id}`,
        name: HOUSE_MODE_NAME_TO_TRANSLATION_KEY[mode.name] || mode.name,
        value: mode._id,
    }));
};

/**
 * Memoized selector that returns the list of house modes.
 *
 * For SECURITY_OEM_ID, always returns SECURITY_MODES (hardcoded).
 * For other OEMs, returns modes from replication API if available,
 * otherwise falls back to HOUSE_MODES (hardcoded).
 *
 * @function
 * @returns {Array} An array of mode objects with:
 *   @property {string} id - Unique identifier for the mode.
 *   @property {string} name - Translation key or mode name.
 *   @property {string} value - Mode value (API _id).
 *
 * @example
 * const modesList = useSelector(houseModesSelector);
 */
export const houseModesSelector = createSelector([replicationModesSelector], (apiModes) => {
    const oemId = oemManager.oem.getOemId();

    if (oemId === OEM_IDS.SECURITY_OEM_ID) {
        return SECURITY_MODES;
    }

    const transformedModes = transformApiModesToUiFormat(apiModes);

    return transformedModes || HOUSE_MODES;
});
